// Demo story: trucks, collection routes, containers, sensors, appetite lines.

import { DEMO_MATERIAL_IDS, MATERIAL_PRICE_SAR_PER_KG } from '../catalog';
import { ids } from '../constants';
import { HOUR_MS, daysAgo, hoursAgo, iso, rngFor } from '../prng';
import { DEMO_COLLECTION_POINTS, DEMO_FACILITIES, DEMO_SITES, type DemoCity } from './places';

// ── Trucks (8: 5 available, 1 in transit, 1 maintenance, 1 loading) ─────────

export type DemoTruckStatus = 'available' | 'in_transit' | 'maintenance' | 'loading';

export interface DemoTruck {
  id: string;
  plate_number: string;
  make: string;
  model: string;
  year: number;
  capacity_kg: number;
  status: DemoTruckStatus;
  driver_id: string | null;
  facility_id: string;
  city: DemoCity;
  fuel_type: 'diesel';
}

// [plate, make, model, year, capacity kg, status, driver n | 0, city]
type TruckRow = [string, string, string, number, number, DemoTruckStatus, number, DemoCity];

const TRUCK_ROWS: TruckRow[] = [
  ['RUA 4821', 'Isuzu', 'NPR 75', 2022, 4500, 'available', 1, 'Riyadh'],
  ['KSA 3317', 'Mitsubishi Fuso', 'Canter', 2021, 3000, 'available', 2, 'Riyadh'],
  ['BHT 7752', 'Hino', '500 Series', 2023, 8000, 'in_transit', 3, 'Riyadh'],
  ['DNX 1209', 'Mercedes-Benz', 'Atego 1218', 2022, 10000, 'available', 4, 'Riyadh'],
  ['LJA 6640', 'Isuzu', 'FVR', 2019, 9000, 'maintenance', 0, 'Riyadh'],
  ['TRE 2085', 'Volvo', 'FL Compactor', 2024, 12000, 'loading', 5, 'Riyadh'],
  ['GZD 5513', 'Hino', '300 Series', 2021, 4000, 'available', 7, 'Jeddah'],
  ['HAV 9036', 'Isuzu', 'NQR', 2020, 5500, 'available', 8, 'Jeddah'],
];

export const DEMO_TRUCKS: DemoTruck[] = TRUCK_ROWS.map(
  ([plate_number, make, model, year, capacity_kg, status, driverN, city], i) => ({
    id: ids.truck(i + 1),
    plate_number,
    make,
    model,
    year,
    capacity_kg,
    status,
    driver_id: driverN > 0 ? ids.driver(driverN) : null,
    facility_id: city === 'Jeddah' ? ids.facility(2) : ids.facility(1),
    city,
    fuel_type: 'diesel',
  }),
);

export function truckById(id: string): DemoTruck | undefined {
  return DEMO_TRUCKS.find((t) => t.id === id);
}

// ── Collection routes (5) ──────────────────────────────────────────────────

export type DemoWeekday = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

/** Indexed by Date#getUTCDay() (0 = Sunday). */
export const DEMO_WEEKDAYS: DemoWeekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export interface DemoRouteStop {
  collection_point_id: string;
  seq: number;
}

/** A recycler collection route (named so it never clashes with the router's DemoRoute). */
export interface DemoCollectionRoute {
  id: string;
  name_en: string;
  name_ar: string;
  zone_id: string;
  truck_id: string;
  facility_id: string;
  stops: DemoRouteStop[];
  schedule: { days: DemoWeekday[]; start_time: string };
  status: 'active';
  distance_km: number;
  est_duration_min: number;
  /** Facility -> stops -> facility, [lng, lat][] (GeoJSON order). */
  geometry: Array<[number, number]>;
}

// [name_en, name_ar, zone n, truck n, facility n, collection point numbers, days, start]
type RouteRow = [string, string, number, number, number, number[], DemoWeekday[], string];

const ROUTE_ROWS: RouteRow[] = [
  ['Riyadh North Morning Loop', 'جولة شمال الرياض الصباحية', 1, 1, 1, [1, 7, 2, 11], ['sun', 'tue', 'thu', 'sat'], '06:30'],
  ['Riyadh North Schools Run', 'جولة مدارس شمال الرياض', 1, 2, 1, [12, 11, 1], ['sun', 'mon', 'wed'], '08:00'],
  ['Riyadh East Retail Loop', 'جولة شرق الرياض التجارية', 2, 3, 1, [3, 10, 4, 8], ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'], '07:00'],
  ['Riyadh East Healthcare Run', 'جولة شرق الرياض الصحية', 2, 4, 1, [8, 9, 4], ['mon', 'wed', 'fri', 'sat'], '09:00'],
  ['Jeddah Coastal Loop', 'جولة جدة الساحلية', 3, 7, 2, [5, 6], ['sun', 'tue', 'thu', 'fri', 'sat'], '07:30'],
];

function haversineKm(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Total length of a [lng, lat][] polyline in km. */
export function polylineKm(geometry: Array<[number, number]>): number {
  let km = 0;
  for (let i = 1; i < geometry.length; i += 1) km += haversineKm(geometry[i - 1], geometry[i]);
  return km;
}

export const DEMO_ROUTES: DemoCollectionRoute[] = ROUTE_ROWS.map(
  ([name_en, name_ar, zoneN, truckN, facilityN, pointNs, days, start_time], i) => {
    const facility = DEMO_FACILITIES[facilityN - 1];
    const stops = pointNs.map((n, seq) => ({ collection_point_id: ids.collectionPoint(n), seq: seq + 1 }));
    const depot: [number, number] = [facility.lng, facility.lat];
    const geometry: Array<[number, number]> = [
      depot,
      ...pointNs.map((n): [number, number] => {
        const p = DEMO_COLLECTION_POINTS[n - 1];
        return [p.lng, p.lat];
      }),
      depot,
    ];
    // Road distance ~1.3x straight line; ~28 km/h urban average plus 15 min per stop.
    const distance = polylineKm(geometry) * 1.3;
    return {
      id: ids.route(i + 1),
      name_en,
      name_ar,
      zone_id: ids.zone(zoneN),
      truck_id: ids.truck(truckN),
      facility_id: facility.id,
      stops,
      schedule: { days: [...days], start_time },
      status: 'active',
      distance_km: Math.round(distance * 10) / 10,
      est_duration_min: Math.round((distance / 28) * 60 + stops.length * 15),
      geometry,
    };
  },
);

export function routeById(id: string): DemoCollectionRoute | undefined {
  return DEMO_ROUTES.find((r) => r.id === id);
}

// ── Containers (26 across the 6 sites) + sensors (8) ───────────────────────

export interface DemoContainer {
  id: string;
  code: string;
  site_id: string;
  material_id: string;
  capacity_litres: 660 | 1100 | 2500;
  /** 0-100 % */
  fill_level: number;
  sensor_id: string | null;
  status: 'active';
  last_emptied_at: string;
}

export interface DemoSensor {
  id: string;
  serial_number: string;
  model: string;
  site_id: string;
  container_id: string;
  status: 'online' | 'offline' | 'low_battery';
  battery_pct: number;
  signal_dbm: number;
  fill_level: number;
  last_seen_at: string;
}

const M = DEMO_MATERIAL_IDS;

// Per site: [material id, litres] for each container; sensored container indexes.
const CONTAINER_PLAN: Array<{ bins: Array<[string, DemoContainer['capacity_litres']]>; sensored: number[] }> = [
  // Olaya Flagship (6)
  { bins: [[M.occ, 2500], [M.occ, 1100], [M.pet, 1100], [M.mixed_paper, 1100], [M.aluminium_cans, 660], [M.uco, 660]], sensored: [0, 2] },
  // Al Malqa (4)
  { bins: [[M.occ, 1100], [M.pet, 1100], [M.ldpe_film, 660], [M.hdpe, 660]], sensored: [0] },
  // Al Nasim (4)
  { bins: [[M.occ, 1100], [M.mixed_paper, 1100], [M.pet, 660], [M.uco, 660]], sensored: [0] },
  // Exit 10 Distribution Centre (6)
  { bins: [[M.occ, 2500], [M.occ, 2500], [M.ldpe_film, 2500], [M.wood_pallets, 2500], [M.office_paper, 1100], [M.hdpe, 1100]], sensored: [0, 2] },
  // Al Rawdah (3)
  { bins: [[M.occ, 1100], [M.pet, 1100], [M.aluminium_cans, 660]], sensored: [0] },
  // Al Salamah (3)
  { bins: [[M.occ, 1100], [M.mixed_paper, 660], [M.uco, 660]], sensored: [0] },
];

function buildContainersAndSensors(): { containers: DemoContainer[]; sensors: DemoSensor[] } {
  const rng = rngFor('containers');
  const containers: DemoContainer[] = [];
  const sensors: DemoSensor[] = [];
  CONTAINER_PLAN.forEach((plan, siteIdx) => {
    const site = DEMO_SITES[siteIdx];
    plan.bins.forEach(([material_id, capacity_litres], binIdx) => {
      const id = ids.container(containers.length + 1);
      const fill_level = rng.int(8, 96);
      let sensor_id: string | null = null;
      if (plan.sensored.includes(binIdx)) {
        const n = sensors.length + 1;
        sensor_id = ids.sensor(n);
        const status: DemoSensor['status'] = n === 5 ? 'low_battery' : n === 8 ? 'offline' : 'online';
        sensors.push({
          id: sensor_id,
          serial_number: `RCL-US-${String(2400 + n).padStart(5, '0')}`,
          model: 'Ultrasonic Fill Sensor US-200',
          site_id: site.id,
          container_id: id,
          status,
          battery_pct: status === 'low_battery' ? 12 : rng.int(55, 98),
          signal_dbm: rng.int(-95, -62),
          fill_level,
          last_seen_at: iso(status === 'offline' ? hoursAgo(rng.int(30, 52)) : new Date(Date.now() - rng.int(2, 25) * 60000)),
        });
      }
      containers.push({
        id,
        code: `${site.code}-C${String(binIdx + 1).padStart(2, '0')}`,
        site_id: site.id,
        material_id,
        capacity_litres,
        fill_level,
        sensor_id,
        status: 'active',
        last_emptied_at: iso(new Date(daysAgo(rng.int(1, 9)).getTime() + rng.int(7, 17) * HOUR_MS)),
      });
    });
  });
  return { containers, sensors };
}

const BUILT = buildContainersAndSensors();

export const DEMO_CONTAINERS: DemoContainer[] = BUILT.containers;
export const DEMO_SENSORS: DemoSensor[] = BUILT.sensors;

export function containersAtSite(siteId: string): DemoContainer[] {
  return DEMO_CONTAINERS.filter((c) => c.site_id === siteId);
}

// ── Recycler appetite (6 weekly lines) ─────────────────────────────────────

export interface DemoAppetiteLine {
  id: string;
  material_id: string;
  target_kg: number;
  period: 'weekly';
  price_sar_per_kg: number;
}

const APPETITE_ROWS: Array<[string, number]> = [
  [M.occ, 12000],
  [M.pet, 4000],
  [M.hdpe, 2000],
  [M.aluminium_cans, 1500],
  [M.steel_scrap, 3000],
  [M.mixed_paper, 5000],
];

export const DEMO_APPETITE_LINES: DemoAppetiteLine[] = APPETITE_ROWS.map(([material_id, target_kg], i) => ({
  id: ids.appetiteLine(i + 1),
  material_id,
  target_kg,
  period: 'weekly',
  price_sar_per_kg: MATERIAL_PRICE_SAR_PER_KG[material_id],
}));
